'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StatsTicker from '@/components/StatsTicker';
import StickyBottomBar from '@/components/StickyBottomBar';
import ToastNotification from '@/components/ToastNotification';
import InterstitialModal from '@/components/InterstitialModal';
import ArticleCard from '@/components/ArticleCard';
import NewsletterBlock from '@/components/NewsletterBlock';
import AdCard from '@/components/AdCard';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ChevronRight, TrendingUp } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const CATEGORIES = ['Metabolic Research', 'Recovery & Healing', 'Growth Factor', 'Cosmetic & Longevity', 'Cognitive Research', 'Compounding'];

interface DbArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  category_type: string;
  tag: string;
  excerpt: string;
  body: string;
  author: string;
  published_at: string;
  read_time: number;
  hero_image_url: string;
}

function dbToArticle(a: DbArticle) {
  return {
    id: a.id,
    slug: a.slug,
    title: a.title,
    category: a.category,
    categoryType: (a.category_type as 'hot' | 'analysis'),
    subTag: (a.tag as 'HOT' | 'EXCLUSIVE' | 'ANALYSIS' | 'REPORT' | 'BREAKING' | 'SPONSORED'),
    excerpt: a.excerpt || '',
    body: a.body || '',
    author: a.author,
    authorTitle: 'Staff Writer',
    date: new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    readTime: a.read_time || 5,
    image: a.hero_image_url || 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&q=80',
    imageAlt: `${a.title} — featured article image`,
    relatedSlugs: [] as string[],
  };
}

export default function HomePage() {
  const [articles, setArticles] = useState<ReturnType<typeof dbToArticle>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArticles() {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('articles')
          .select('id, slug, title, category, category_type, tag, excerpt, body, author, published_at, read_time, hero_image_url')
          .order('published_at', { ascending: false })
          .limit(20);
        setArticles((data || []).map(dbToArticle));
      } catch {
        // Silently fail — page still renders
      } finally {
        setLoading(false);
      }
    }
    fetchArticles();
  }, []);

  const heroArticle = articles[0];
  const gridArticles = articles.slice(1, 7);
  const latestArticles = articles.slice(0, 8);

  return (
    <>
      <Header />
      <StatsTicker />
      <main className="pb-16">
        {/* Hero Carousel — featured article */}
        <section className="relative h-[520px] lg:h-[600px] overflow-hidden">
          {heroArticle ? (
            <>
              <AppImage
                src={heroArticle.image}
                alt={heroArticle.imageAlt}
                fill
                priority
                sizes="100vw"
                className="object-cover"
              />
              <div className="absolute inset-0 hero-gradient" />
              <div className="absolute inset-0 flex items-end">
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pb-10 w-full">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="tag-hot text-xs font-700 px-2 py-1 rounded uppercase tracking-wide">
                        {heroArticle.subTag}
                      </span>
                      <span className="text-white/70 text-xs font-600 uppercase tracking-wide">
                        {heroArticle.category}
                      </span>
                    </div>
                    <h1 className="text-hero-md font-800 text-white leading-tight">
                      {heroArticle.title}
                    </h1>
                    <p className="mt-3 text-white/80 text-sm leading-relaxed line-clamp-2">
                      {heroArticle.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-4">
                      <Link
                        href={`/article-detail-page?slug=${heroArticle.slug}`}
                        className="btn-primary"
                      >
                        Read Full Story <ChevronRight size={16} />
                      </Link>
                      <span className="text-white/60 text-xs">
                        {heroArticle.author} · {heroArticle.date}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 bg-foreground flex items-center justify-center">
              {loading && <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />}
            </div>
          )}
        </section>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          {/* Story card grid */}
          <section id="news" className="py-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-700 text-foreground">Latest Coverage</h2>
              <Link href="/search?q=" className="text-sm font-600 text-primary hover:text-primary-dark flex items-center gap-1">
                All Articles <ChevronRight size={14} />
              </Link>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1,2,3,4,5,6].map((i) => (
                  <div key={i} className="bg-card border border-border rounded-lg h-64 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
                {gridArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </section>

          {/* Mid-feed ad */}
          <div className="mb-8">
            <AdCard
              label="SPONSORED"
              headline="Reach 200K+ Peptide Industry Professionals"
              subtext="Native placements, interstitials, and newsletter sponsorships. View our media kit."
              ctaText="View Advertise Options"
              ctaHref="/advertise-page"
              image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              imageAlt="Advertisement for USA Peptide Pros advertising placements reaching industry professionals"
            />
          </div>

          {/* Latest News + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp size={18} className="text-primary" />
                <h2 className="text-xl font-700">Latest News</h2>
              </div>
              <div className="divide-y divide-border">
                {latestArticles.map((article) => (
                  <Link
                    key={article.id}
                    href={`/article-detail-page?slug=${article.slug}`}
                    className="flex gap-4 py-4 group"
                  >
                    <div className="flex-shrink-0 w-20 h-14 rounded overflow-hidden relative">
                      <AppImage
                        src={article.image}
                        alt={article.imageAlt}
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-700 px-1.5 py-0.5 rounded uppercase tracking-wide ${
                            article.categoryType === 'hot' ? 'tag-hot' : 'tag-editorial'
                          }`}
                        >
                          {article.category}
                        </span>
                      </div>
                      <p className="text-sm font-600 text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">{article.date} · {article.readTime} min read</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              <div className="bg-secondary rounded-lg p-5 border border-border">
                <p className="section-eyebrow mb-3">Category Spotlight</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <Link
                      key={`cat-${cat}`}
                      href={`/search?q=${encodeURIComponent(cat)}`}
                      className="text-xs font-600 px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
              <NewsletterBlock compact />
              <AdCard
                label="ADVERTISEMENT"
                headline="FDA Guidance Tracker — Free Access"
                subtext="Track every compounded peptide guidance item in one place."
                ctaText="View Tracker"
                ctaHref="/fda-guidance-tracker-page"
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
      <ToastNotification
        message="BREAKING: FDA expands Import Alert 66-40 — 38 new facilities flagged"
        linkText="Read the full story"
        linkHref="/article-detail-page?slug=fda-import-alert-66-40-peptide-apis"
      />
      <InterstitialModal />
    </>
  );
}